import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Flex,
  Icon,
  Markdown,
  Text,
} from '@kognitos/lattice';
import { useGetGuideEntry } from '@/generated/hooks/astral/v1/api';
import type { IParsedGuideEntry } from '@/shared/utils/chat-utils';
import { parseResourceName } from '@/shared/utils/api-utils';

/**
 * Guide entry state values from the API.
 */
export enum EGuideEntryState {
  UNSPECIFIED = 'STATE_UNSPECIFIED',
  PENDING_APPROVAL = 'STATE_PENDING_APPROVAL',
  APPROVED = 'STATE_APPROVED',
  REJECTED = 'STATE_REJECTED',
  ARCHIVED = 'STATE_ARCHIVED',
}

/**
 * Props for the InlineGuideEntryWidget component.
 */
interface IInlineGuideEntryWidgetProps {
  /** Parsed guide entry data (used as fallback if API fetch fails) */
  guideEntry: IParsedGuideEntry;
  /**
   * Labels an approved entry as "Applied" (used when showing the guide entry
   * that resolved an exception).
   */
  readOnly?: boolean;
}

/**
 * Parses a guide entry resource name into its components.
 *
 * @param name - Full resource name (e.g., 'organizations/org/workspaces/ws/automations/auto/guideEntries/id')
 * @returns Object with organization, workspace, automation, and guideEntry IDs
 */
function parseGuideEntryName(name: string): {
  organization: string;
  workspace: string;
  automation: string;
  guideEntry: string;
} {
  const parsed = parseResourceName(name);
  return {
    organization: parsed.organization || '',
    workspace: parsed.workspace || '',
    automation: parsed.automation || '',
    guideEntry: parsed.guideEntry || '',
  };
}

/**
 * Determines if the guide entry is in approved state.
 *
 * @param state - The guide entry state string
 * @returns True if approved
 */
function isApproved(state: string): boolean {
  return state === EGuideEntryState.APPROVED;
}

/**
 * Determines if the guide entry is in a terminal state (rejected or archived).
 *
 * @param state - The guide entry state string
 * @returns True if in terminal state
 */
function isTerminalState(state: string): boolean {
  return (
    state === EGuideEntryState.REJECTED || state === EGuideEntryState.ARCHIVED
  );
}

/**
 * Presentational widget for a guide entry. Display-only — actions (approve /
 * reject / link to the playbook) are rendered by the caller outside the widget
 * ({@link GuideEntryApprovalPills} in the astral flow, buttons in the Rain
 * approval card).
 *
 * Fetches the latest guide entry data from the API to ensure the widget displays
 * current state. Falls back to parsed XML data if the API call fails.
 *
 * Shows a collapsible card with:
 * - Collapsed: Title with state badge
 * - Expanded: Root cause and guidance, plus a terminal-state note when
 *   rejected/archived
 *
 * @param props - Component props
 * @returns Guide entry preview widget
 */
function InlineGuideEntryWidget({
  guideEntry,
  readOnly = false,
}: IInlineGuideEntryWidgetProps) {
  const { t } = useTranslation('guidance');
  const { t: tCommon } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);

  // Parse resource name to get IDs for API call
  const {
    organization,
    workspace,
    automation,
    guideEntry: guideEntryId,
  } = useMemo(() => parseGuideEntryName(guideEntry.name), [guideEntry.name]);

  // Fetch latest guide entry data from API
  const { data: guideEntryResponse } = useGetGuideEntry(
    organization,
    workspace,
    automation,
    guideEntryId,
    {
      query: {
        enabled: !!(organization && workspace && automation && guideEntryId),
        staleTime: 0, // Always fetch fresh data
        refetchOnMount: true,
      },
    },
  );

  // Use API data if available, otherwise fall back to parsed XML data
  // Prefer resolution_steps (new field) but fall back to content (legacy) for backward compatibility
  const currentData = useMemo(() => {
    const apiData = guideEntryResponse?.data;
    if (apiData) {
      return {
        title: apiData.title || guideEntry.title,
        rootCause: apiData.root_cause || guideEntry.rootCause,
        resolutionSteps:
          apiData.resolution_steps ||
          guideEntry.resolutionSteps ||
          apiData.content ||
          guideEntry.content ||
          '',
        state: apiData.state || guideEntry.state,
        action: guideEntry.action, // Action type comes from XML only
      };
    }
    return {
      title: guideEntry.title,
      rootCause: guideEntry.rootCause,
      resolutionSteps: guideEntry.resolutionSteps || guideEntry.content || '',
      state: guideEntry.state,
      action: guideEntry.action,
    };
  }, [guideEntryResponse?.data, guideEntry]);

  const isApprovedState = isApproved(currentData.state);
  const isTerminal = isTerminalState(currentData.state);

  // Determine badge label based on state and action
  const getBadgeLabel = () => {
    if (isTerminal) {
      return currentData.state === EGuideEntryState.REJECTED
        ? t('guidance.troubleshooting.widget.stateRejected')
        : t('guidance.troubleshooting.widget.stateArchived');
    }
    if (isApprovedState) {
      return readOnly
        ? t('guidance.troubleshooting.widget.stateApplied')
        : t('guidance.troubleshooting.widget.stateApproved');
    }
    // Pending - show action type
    if (currentData.action === 'create') {
      return t('guidance.troubleshooting.widget.proposedNew');
    }
    if (currentData.action === 'use') {
      return t('guidance.troubleshooting.widget.proposedUse');
    }
    return t('guidance.troubleshooting.widget.proposedUpdate');
  };

  // Determine badge variant based on state
  const getBadgeVariant = (): 'secondary' | 'outline' | 'destructive' => {
    if (currentData.state === EGuideEntryState.REJECTED) {
      return 'destructive';
    }
    if (isApprovedState) {
      return 'outline';
    }
    return 'secondary';
  };

  return (
    <div className="w-full rounded-lg border border-border/50 bg-card/50 overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {/* Header - always visible, not clickable */}
        <Flex align="start" gap={2} width="full" padding={3}>
          <Icon
            type="BookOpen"
            size={16}
            className="text-muted-foreground shrink-0 mt-0.5"
          />
          <Flex direction="column" gap={1} flex="1" className="min-w-0">
            <Flex
              align="start"
              gap={2}
              wrap="wrap"
              width="full"
              justify="between"
            >
              <Text level="small" weight="medium" className="flex-1">
                {currentData.title}
              </Text>
              <Badge variant={getBadgeVariant()} className="text-xs shrink-0">
                {getBadgeLabel()}
              </Badge>
            </Flex>
          </Flex>
        </Flex>

        <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
          <Flex
            direction="column"
            gap={4}
            paddingX={3}
            paddingY={2}
            className="border-t border-border/30"
          >
            {/* Root Cause Section */}
            {currentData.rootCause && (
              <Flex direction="column" gap={1.5}>
                <Text weight="medium" level="small" color="muted">
                  {t('guidance.troubleshooting.detail.rootCause')}
                </Text>
                <div className="[&_p]:text-sm [&_li]:text-sm">
                  <Markdown>{currentData.rootCause}</Markdown>
                </div>
              </Flex>
            )}

            {/* Resolution Steps Section */}
            {currentData.resolutionSteps && (
              <Flex direction="column" gap={1.5}>
                <Text weight="medium" level="small" color="muted">
                  {t('guidance.troubleshooting.detail.guidance')}
                </Text>
                <div className="[&_p]:text-sm [&_li]:text-sm">
                  <Markdown>{currentData.resolutionSteps}</Markdown>
                </div>
              </Flex>
            )}
          </Flex>
        </CollapsibleContent>

        {/* Single always-present toggle. Placed outside CollapsibleContent so
        nothing is inserted/removed mid-animation — the content animates its
        height (top-anchored) and this trigger rides along at the bottom. */}
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-7 rounded-none border-t border-border/30 text-muted-foreground hover:text-foreground"
          >
            <Icon type={isOpen ? 'Minimize' : 'Maximize'} size="sm" />
            {isOpen
              ? tCommon('common.action.showLess')
              : tCommon('common.action.showMore')}
          </Button>
        </CollapsibleTrigger>
      </Collapsible>
    </div>
  );
}

export default InlineGuideEntryWidget;
